
import { Component, Vue } from 'vue-property-decorator'
import request from '@/utils/request'
import Uploader from '../../utils/uploader'
import { OtaFileResponse } from '@/wwapi/models/get-all-ota-files-response'
import { BetaGroup, getBetaGroups } from '@/api/betaGroups'
import moment from 'moment'
import XLSX from 'xlsx'
import {
  listUserOtaOverrides,
  clearUserOtaOverride,
  getUserOtaInfo
} from '@/api/userOtaOverride'
import PinList, { PinListItem } from '@/components/ota/PinList.vue'
import PinnedDevicesPanel from '@/components/ota/PinnedDevicesPanel.vue'
import PinForm from '@/components/ota/PinForm.vue'
import UserSearch from '@/components/UserSearch/index.vue'

interface OtaForm {
  wirelessFile: File | null
  applicationFile: File | null
  description: string
  wirelessVersionName: string
  applicationVersionName: string
  installOrder: string
  groupIds: number[]
  releaseNotes: string
  isMandatory: boolean
  rolloutPercentage: number
  rolloutStrategy: string
  isPinOnly: boolean
  overridesOlderPins: boolean
  [key: string]: any
}

@Component({ components: { PinList, PinForm, UserSearch, PinnedDevicesPanel } })
export default class extends Vue {
  // Pin Yönet drawer — opened from the "Pin Yönet" action button. Shows every
  // user pinned to this OTA file (PinList) and a new-pin form that picks an
  // arbitrary user (UserSearch) then their cane (PinForm).
  private pinDrawerVisible = false
  private pinDrawerOta: any = null
  private pinDrawerPins: PinListItem[] = []
  private pinListLoading = false
  private pinRemovingKey: string | null = null
  private newPinUserId = ''
  private pickedUserDevices: any[] = []
  private pickedUserPins: PinListItem[] = []
  private newPinUserLoading = false

  private uploadModalVisible = false;
  private loading = false;
  private list: any[] = [];
  private filteredList: any[] = [];
  private totalItems = 0;
  private currentPage = 1;
  private pageSize = 10;
  private betaGroups: BetaGroup[] = [];
  private filterGroupId: number | string | null = null;
  private otaForm: OtaForm = {
    wirelessFile: null,
    applicationFile: null,
    description: '',
    wirelessVersionName: '',
    applicationVersionName: '',
    installOrder: '',
    groupIds: [],
    releaseNotes: '',
    isMandatory: false,
    rolloutPercentage: 100,
    rolloutStrategy: 'random',
    isPinOnly: false,
    overridesOlderPins: false
  };

  // Tab state for the OTA page: builds list vs the pin manager.
  private activeTab = 'files';

  private editModalVisible = false;
  private editLoading = false;
  private editForm: any = {
    id: null,
    isActive: true,
    isMandatory: false,
    groupIds: [],
    rolloutPercentage: 100,
    rolloutStrategy: 'random',
    releaseNotes: '',
    description: '',
    isPinOnly: false,
    overridesOlderPins: false
  };

  // Beta groups pickable in the OTA form. The default ("Stable Release") group
  // is hidden on purpose: leaving the field empty already means the stable
  // channel (group_id NULL = whole fleet). Tagging a build to the default
  // group would strand it (default-group members resolve to the null channel
  // at serve time), so we only offer real, non-default beta groups.
  private get selectableGroups(): BetaGroup[] {
    return (this.betaGroups || []).filter((g) => !g.isDefault)
  }

  private logsModalVisible = false;
  private logsLoading = false;
  private logsExporting = false;
  private logsVersionLabel = '';
  private logsOtaFileId: number | null = null;
  private downloadLogs: any[] = [];
  private logsUniqueUserCount = 0;
  // NULL serials excluded so the count reflects "canes we can identify"
  // (mobile started passing deviceSerial only after R35 / device_serial fix).
  private logsUniqueDeviceCount = 0;
  private logsPagination: {
    total: number
    totalRequests: number
    page: number
    limit: number
    totalPages: number
  } = {
    total: 0,
    totalRequests: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  };

  private listLoading = true;

  private rules = {
    wirelessFile: [
      {
        required: true,
        message: 'Please select a Wireless file',
        trigger: 'change'
      }
    ],
    applicationFile: [
      {
        required: true,
        message: 'Please select an Application file',
        trigger: 'change'
      }
    ],
    wirelessVersionName: [
      {
        required: true,
        message: 'Please enter a Wireless version name',
        trigger: 'blur'
      },
      {
        pattern: /^[0-9]+\.[0-9]+\.[0-9]+$/,
        message: 'Version must be in the format 1.1.1000',
        trigger: 'blur'
      }
    ],
    applicationVersionName: [
      {
        required: true,
        message: 'Please enter an Application version name',
        trigger: 'blur'
      },
      {
        pattern: /^[0-9]+\.[0-9]+\.[0-9]+$/,
        message: 'Version must be in the format 1.1.1000',
        trigger: 'blur'
      }
    ],
    installOrder: [
      {
        required: true,
        message: 'Please select an Install Priority',
        trigger: 'change'
      }
    ]
  };

  created() {
    this.fetchOtaVersions()
    this.fetchBetaGroups()
  }

  private async fetchBetaGroups() {
    try {
      const res: any = await getBetaGroups()
      // Backend envelopes admin endpoints as { status, data: [...] }; res.data
      // is the envelope, the array lives one level deeper.
      this.betaGroups = res.data?.data || []
    } catch {
      // silent
    }
  }

  private showUploadModal() {
    this.otaForm = {
      wirelessFile: null,
      applicationFile: null,
      description: '',
      wirelessVersionName: '',
      applicationVersionName: '',
      installOrder: '',
      groupIds: [],
      releaseNotes: '',
      isMandatory: false,
      rolloutPercentage: 100,
      rolloutStrategy: 'random',
      isPinOnly: false,
      overridesOlderPins: false
    }
    this.uploadModalVisible = true
  }

  private handleWirelessFileChange(event: Event) {
    const target = event.target as HTMLInputElement
    if (target.files && target.files.length > 0) {
      this.otaForm.wirelessFile = target.files[0]
    }
  }

  private handleApplicationFileChange(event: Event) {
    const target = event.target as HTMLInputElement
    if (target.files && target.files.length > 0) {
      this.otaForm.applicationFile = target.files[0]
    }
  }

  private validateVersionInput(field: string) {
    const value = this.otaForm[field]
    if (/[^0-9.]/.test(value)) {
      this.otaForm[field] = value.replace(/[^0-9.]/g, '')
    }
  }

  private formatDate(date: string) {
    return moment(date).format('DD.MM.YYYY HH:mm')
  }

  private formatDay(date: string) {
    return moment(date).format('DD.MM.YYYY')
  }

  private handlePageChange(page: number) {
    this.currentPage = page
  }

  private applyFilter() {
    this.currentPage = 1
    if (this.filterGroupId === null || this.filterGroupId === '') {
      this.filteredList = this.list
    } else if (this.filterGroupId === 'default') {
      // Stable channel = a build with no beta group rows at all.
      this.filteredList = this.list.filter((item: any) => !(item.groupIds && item.groupIds.length))
    } else {
      this.filteredList = this.list.filter(
        (item: any) => (item.groupIds || []).indexOf(this.filterGroupId) !== -1
      )
    }
    this.totalItems = this.filteredList.length
  }

  private async submitOtaVersion() {
    (this.$refs.otaForm as any).validate(async(valid: boolean) => {
      if (!valid) {
        this.$message.error('Please fill all required fields.')
        return
      }

      if (!this.otaForm.wirelessFile || !this.otaForm.applicationFile) {
        this.$message.error(
          'Please select both Wireless and Application files.'
        )
        return
      }

      if (
        !this.otaForm.wirelessVersionName ||
        !this.otaForm.applicationVersionName
      ) {
        this.$message.error(
          'Please enter both Wireless and Application version numbers.'
        )
        return
      }

      this.loading = true

      try {
        const wirelessFile = this.otaForm.wirelessFile
        const wirelessFileName = `${Date.now()}-${wirelessFile.name}`
        const wirelessFileType = wirelessFile.type || 'application/octet-stream'
        const wirelessFileSize = wirelessFile.size

        const applicationFile = this.otaForm.applicationFile
        const applicationFileName = `${Date.now()}-${applicationFile.name}`
        const applicationFileType = applicationFile.type || 'application/octet-stream'
        const applicationFileSize = applicationFile.size

        const uploader = new Uploader()

        const wirelessResponse = await uploader.uploadFile({
          file: wirelessFile,
          fileName: wirelessFileName,
          contentType: wirelessFileType
        })

        const applicationResponse = await uploader.uploadFile({
          file: applicationFile,
          fileName: applicationFileName,
          contentType: applicationFileType
        })

        const wirelessUrl = wirelessResponse.Location
        const applicationUrl = applicationResponse.Location

        const otaData: any = {
          wirelessFileName: wirelessFileName,
          wirelessFileSize: wirelessFileSize,
          wirelessUploadDate: new Date().toISOString(),
          wirelessAwsUrl: wirelessUrl,
          wirelessVersion: 1,
          wirelessVersionName: this.otaForm.wirelessVersionName,
          applicationFileName: applicationFileName,
          applicationFileSize: applicationFileSize,
          applicationUploadDate: new Date().toISOString(),
          applicationAwsUrl: applicationUrl,
          applicationVersion: 1,
          applicationVersionName: this.otaForm.applicationVersionName,
          installOrder: this.otaForm.installOrder,
          description: this.otaForm.description,
          releaseNotes: this.otaForm.releaseNotes,
          isMandatory: this.otaForm.isMandatory,
          rolloutPercentage: this.otaForm.rolloutPercentage,
          rolloutStrategy: this.otaForm.rolloutStrategy,
          isPinOnly: this.otaForm.isPinOnly,
          overridesOlderPins: this.otaForm.overridesOlderPins
        }

        // Always send the array, including when empty: an empty list means
        // the stable channel, which is a meaningful choice and not "unset".
        otaData.groupIds = this.otaForm.groupIds || []

        await request.post('/otafiles/createOtaVersion', otaData)

        this.$message.success('OTA version created successfully.')
        this.uploadModalVisible = false
        this.fetchOtaVersions()
      } catch (error: any) {
        console.error('Error: ', error.response?.data || error.message)
        this.$message.error('Failed to create OTA version.')
      } finally {
        this.loading = false
      }
    })
  }

  private async fetchOtaVersions() {
    this.listLoading = true
    try {
      const response = await request.get('/otafiles/all', { timeout: 30000 })
      const responseModel: any[] = response.data
      this.list = responseModel
      this.filteredList = responseModel
      this.totalItems = responseModel.length
    } catch (error) {
      this.$message.error('Failed to fetch OTA versions.')
    } finally {
      this.listLoading = false
    }
  }

  private showEditModal(row: any) {
    this.editForm = {
      id: row.id,
      isActive: row.isActive,
      isMandatory: row.isMandatory,
      groupIds: Array.isArray(row.groupIds) ? row.groupIds.slice() : (row.groupId != null ? [row.groupId] : []),
      rolloutPercentage: row.rolloutPercentage != null ? row.rolloutPercentage : 100,
      rolloutStrategy: row.rolloutStrategy || 'random',
      releaseNotes: row.releaseNotes || '',
      description: row.description || '',
      isPinOnly: row.isPinOnly === true,
      overridesOlderPins: row.overridesOlderPins === true
    }
    this.editModalVisible = true
  }

  private async submitEdit() {
    if (!this.editForm.id) return
    this.editLoading = true
    try {
      const { id, ...data } = this.editForm
      await request.put(`/otafiles/${id}`, data)
      this.$message.success('OTA file updated successfully.')
      this.editModalVisible = false
      this.fetchOtaVersions()
    } catch (error: any) {
      this.$message.error('Failed to update OTA file.')
    } finally {
      this.editLoading = false
    }
  }

  private confirmDelete(row: any) {
    this.$confirm(
      `Are you sure you want to delete OTA version ${row.applicationVersionName}? This will also delete the firmware files from storage. This action cannot be undone.`,
      'Delete OTA File',
      {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }
    ).then(async() => {
      try {
        await request.delete(`/otafiles/${row.id}`)
        this.$message.success('OTA file deleted successfully.')
        this.fetchOtaVersions()
      } catch (error: any) {
        this.$message.error('Failed to delete OTA file.')
      }
    }).catch(() => {
      // cancelled
    })
  }

  private async showDownloadLogs(row: any) {
    this.logsOtaFileId = row.id
    this.logsVersionLabel = `v${row.applicationVersionName}`
    this.logsPagination.page = 1
    this.logsModalVisible = true
    await this.fetchDownloadLogs()
  }

  private async fetchDownloadLogs() {
    this.logsLoading = true
    try {
      const params: any = {
        page: this.logsPagination.page,
        limit: this.logsPagination.limit
      }
      if (this.logsOtaFileId) {
        params.otaFileId = this.logsOtaFileId
      }
      const response = await request.get('/otafiles/download-logs', { params })
      this.downloadLogs = response.data.data
      this.logsPagination = response.data.pagination

      // Calculate unique users + devices from current page. Both counts are
      // per-page (not global) — matches existing `Unique Users` behavior and
      // avoids a second backend round-trip just for an aggregate.
      const uniqueUsers = new Set(this.downloadLogs.map((l: any) => l.userId))
      this.logsUniqueUserCount = uniqueUsers.size
      const uniqueDevices = new Set(
        this.downloadLogs
          .map((l: any) => l.deviceSerialNumber)
          .filter((s: any) => !!s)
      )
      this.logsUniqueDeviceCount = uniqueDevices.size
    } catch (error) {
      this.$message.error('Failed to fetch download logs.')
    } finally {
      this.logsLoading = false
    }
  }

  private handleLogsPageChange(page: number) {
    this.logsPagination.page = page
    this.fetchDownloadLogs()
  }

  private userTooltip(u: any): string {
    if (!u) return ''
    const name = `${(u.firstName || '').trim()} ${(u.lastName || '').trim()}`.trim()
    return [name, u.email, u.userId].filter(Boolean).join(' • ')
  }

  // Pull the full filtered set (cap 50k server-side) and build an .xlsx
  // locally. Two reasons over a server-streamed download:
  //   1. The admin panel already ships `xlsx` for other exports, no new dep.
  //   2. Operators can re-arrange columns in Excel without us shipping a
  //      backend formatting change for every request.
  private async exportLogsToExcel() {
    if (!this.logsOtaFileId) {
      this.$message.warning(this.$t('otaFilesExtra.noOtaSelected') as string)
      return
    }
    this.logsExporting = true
    try {
      const params: any = { otaFileId: this.logsOtaFileId }
      const response = await request.get('/otafiles/download-logs/export', { params })
      const rows: any[] = response.data?.data || []
      if (!rows.length) {
        this.$message.info(this.$t('otaFilesExtra.noRecordsToExport') as string)
        return
      }
      // Flatten the user object into named columns so Excel filters work
      // without the operator having to dig into nested JSON.
      const sheetRows = rows.map(r => ({
        // Grouped rows surface first/last separately; "Last Request" mirrors
        // the table's primary column so filtering stays intuitive.
        'Last Request': r.last_at
          ? moment(r.last_at).format('YYYY-MM-DD HH:mm:ss')
          : r.created_at ? moment(r.created_at).format('YYYY-MM-DD HH:mm:ss') : '',
        'First Request': r.first_at ? moment(r.first_at).format('YYYY-MM-DD HH:mm:ss') : '',
        'Request Count': r.request_count || 1,
        Email: r.user?.email || '',
        'First Name': r.user?.firstName || '',
        'Last Name': r.user?.lastName || '',
        'User ID': r.userId || '',
        'App Version': r.applicationVersionName || '',
        'Wireless Version': r.wirelessVersionName || '',
        'Device Serial': r.deviceSerialNumber || '',
        'Requested Major': r.requestedMajorVersion || '',
        'IP Address': r.ipAddress || '',
        'User Agent': r.userAgent || ''
      }))
      const ws = XLSX.utils.json_to_sheet(sheetRows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Download Logs')
      const stamp = moment().format('YYYYMMDD_HHmm')
      const label = (this.logsVersionLabel || 'logs').replace(/[^\w.-]/g, '_')
      XLSX.writeFile(wb, `ota-download-logs_${label}_${stamp}.xlsx`)
      this.$message.success(this.$tc('otaFilesExtra.recordsExported', rows.length, { n: rows.length }) as string)
    } catch (err: any) {
      this.$message.error(this.$t('otaFilesExtra.excelExportFailed', { error: err.message || '' }) as string)
    } finally {
      this.logsExporting = false
    }
  }

  private get paginatedList() {
    const start = (this.currentPage - 1) * this.pageSize
    const end = this.currentPage * this.pageSize
    return this.filteredList.slice(start, end)
  }

  // ===== Pin Yönet drawer =====

  get pinDrawerTitle(): string {
    if (!this.pinDrawerOta) return this.$t('otaFilesExtra.managePins') as string
    return this.$t('otaFilesExtra.managePinsFor', { name: this.pinDrawerOta.applicationVersionName || ('id ' + this.pinDrawerOta.id) }) as string
  }

  // PinForm expects an object with at minimum { id, application_version_name,
  // wireless_version_name }. Normalize from this page's camelCase row.
  get lockedOtaForForm(): any {
    if (!this.pinDrawerOta) return null
    return {
      id: this.pinDrawerOta.id,
      application_version_name: this.pinDrawerOta.applicationVersionName,
      wireless_version_name: this.pinDrawerOta.wirelessVersionName,
      is_mandatory: this.pinDrawerOta.isMandatory
    }
  }

  private async showPinManager(row: any) {
    this.pinDrawerOta = row
    this.pinDrawerVisible = true
    this.newPinUserId = ''
    this.pickedUserDevices = []
    this.pickedUserPins = []
    await this.fetchPinsForOta()
  }

  private closePinDrawer(done: () => void) {
    this.pinDrawerVisible = false
    this.pinDrawerOta = null
    this.pinDrawerPins = []
    this.newPinUserId = ''
    this.pickedUserDevices = []
    this.pickedUserPins = []
    done()
  }

  private async fetchPinsForOta() {
    if (!this.pinDrawerOta) return
    this.pinListLoading = true
    try {
      const { data } = await listUserOtaOverrides({
        otaFileId: this.pinDrawerOta.id,
        pageSize: 200
      })
      // listUserOtaOverrides returns camelCase + a hydrated `user` object;
      // normalize into PinListItem so PinList renders the user inline.
      this.pinDrawerPins = (data.data || []).map((row: any) => ({
        key: (row.userId || 'u') + ':' + (row.deviceSerialNumber || 'user-scope'),
        userId: row.userId,
        deviceSerial: row.deviceSerialNumber,
        otaFileId: row.otaFileId,
        appVersion: row.otaFile?.applicationVersionName || '',
        wirelessVersion: row.otaFile?.wirelessVersionName || null,
        reason: row.reason,
        setBy: row.setBy,
        createdAt: row.createdAt,
        userEmail: row.user?.email || row.userId
      }))
    } catch (err: any) {
      this.$message.error(this.$t('otaFilesExtra.pinListLoadFailed', { error: err.message || '' }) as string)
      this.pinDrawerPins = []
    } finally {
      this.pinListLoading = false
    }
  }

  private async onUserPicked(userId: string) {
    this.newPinUserId = userId
    this.pickedUserDevices = []
    this.pickedUserPins = []
    if (!userId) return
    this.newPinUserLoading = true
    try {
      const { data } = await getUserOtaInfo(userId)
      const payload = data.data || {}
      this.pickedUserDevices = payload.devices || []
      this.pickedUserPins = (payload.overrides || []).map((o: any) => ({
        key: o.deviceSerialNumber || 'user-scope',
        deviceSerial: o.deviceSerialNumber,
        otaFileId: o.otaFileId,
        appVersion: o.otaFile?.applicationVersionName || '',
        wirelessVersion: o.otaFile?.wirelessVersionName || null,
        reason: o.reason,
        setBy: o.setBy,
        createdAt: o.createdAt
      }))
    } catch (err: any) {
      this.$message.error(this.$t('otaFilesExtra.userDetailsLoadFailed', { error: err.message || '' }) as string)
    } finally {
      this.newPinUserLoading = false
    }
  }

  private async onNewPinSaved() {
    // Refresh both: the drawer-wide list (added a row) and the picked user's
    // own pin list (PinForm picks up the new state for "update vs create").
    await this.fetchPinsForOta()
    if (this.newPinUserId) await this.onUserPicked(this.newPinUserId)
  }

  private async removePinFromDrawer(p: PinListItem) {
    if (!p.userId) return
    const isUserScope = p.deviceSerial === null
    const scopeLabel = isUserScope ? this.$t('otaFilesExtra.allCanesUserScope') as string : p.deviceSerial
    try {
      await this.$confirm(
        this.$t('otaFilesExtra.removePinConfirm', { version: p.appVersion, scope: scopeLabel, user: p.userEmail || p.userId }) as string,
        this.$t('otaFilesExtra.removePinTitle') as string,
        { confirmButtonText: this.$t('otaFilesExtra.confirmRemove') as string, cancelButtonText: this.$t('otaFilesExtra.cancel') as string }
      )
    } catch {
      return
    }
    this.pinRemovingKey = p.key
    try {
      await clearUserOtaOverride(
        p.userId,
        isUserScope ? undefined : (p.deviceSerial || undefined),
        isUserScope
      )
      this.$message.success(this.$t('otaFilesExtra.pinRemoved') as string)
      await this.fetchPinsForOta()
      if (this.newPinUserId === p.userId) await this.onUserPicked(this.newPinUserId)
    } catch (err: any) {
      this.$message.error(this.$t('otaFilesExtra.pinRemoveFailed', { error: err.message || '' }) as string)
    } finally {
      this.pinRemovingKey = null
    }
  }
}
